import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import pb from '../../lib/pocketbase';
import { useAuth } from '../../contexts/AuthContext';

// Helper for date grouping
const getDayLabel = (dateStr) => {
    const d = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);

    if (d.toDateString() === today.toDateString()) return 'Aujourd\'hui';
    if (d.toDateString() === yesterday.toDateString()) return 'Hier';
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
};

// Helper for avatars
const Avatar = ({ name, email, size = 40 }) => {
    const label = name ? name.charAt(0).toUpperCase() : (email ? email.charAt(0).toUpperCase() : '?');
    const colors = ['#f87171', '#fb923c', '#fbbf24', '#a3e635', '#34d399', '#22d3ee', '#818cf8', '#c084fc', '#f472b6'];
    const colorIndex = label.charCodeAt(0) % colors.length;

    return (
        <div style={{
            width: size, height: size, borderRadius: '50%',
            backgroundColor: colors[colorIndex], color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: size * 0.45, flexShrink: 0
        }}>
            {label}
        </div>
    );
};

export default function AdminMessagesPage() {
    const { user } = useAuth();
    const [conversations, setConversations] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [chatMessages, setChatMessages] = useState([]);
    const [loading, setLoading] = useState(false);

    // Message Input State
    const [replyContent, setReplyContent] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [showSnippets, setShowSnippets] = useState(false);
    const fileInputRef = useRef(null);

    const QUICK_REPLIES = [
        { label: '👋 Bonjour', text: "Bonjour, j'espère que vous allez bien." },
        { label: '👀 Bien reçu', text: "Bien reçu, je regarde ça et je reviens vers vous rapidement." },
        { label: '✅ Devis prêt', text: "Votre devis est prêt, vous pouvez le consulter directement dans votre espace client." },
        { label: '📅 Disponibilités', text: "Quelles seraient vos disponibilités pour un court échange téléphonique ?" },
        { label: '🙏 Merci', text: "Merci beaucoup pour votre retour !" },
        { label: '🚀 Avancement', text: "Le projet avance bien, je vous ferai un point complet d'ici la fin de semaine." }
    ];

    const handleAddSnippet = (text) => {
        setReplyContent(prev => prev + (prev ? "\n" : "") + text);
        setShowSnippets(false);
    };

    // Context Popup State
    const [contextOpen, setContextOpen] = useState(false);
    const [contextData, setContextData] = useState({ projects: [], invoices: [], loading: false, error: null, customer: null });

    const scrollRef = useRef(null);

    // 1. Fetch Conversations
    const fetchConversations = async () => {
        try {
            const res = await pb.collection('messages').getList(1, 500, {
                sort: '-created',
                expand: 'from,to,customer_id',
            });

            const convMap = {};

            res.items.forEach(msg => {
                let otherId = null;
                let otherUser = null;

                if (msg.from === user.id) {
                    otherId = msg.to;
                    otherUser = msg.expand?.to;
                } else {
                    otherId = msg.from;
                    otherUser = msg.expand?.from;
                }

                if (!otherId) return;

                if (!convMap[otherId]) {
                    convMap[otherId] = {
                        userId: otherId,
                        user: otherUser,
                        lastMessage: msg,
                        unreadCount: 0,
                        customer: msg.expand?.customer_id
                    };
                }

                if (msg.from === otherId && !msg.read) {
                    convMap[otherId].unreadCount++;
                }
            });

            setConversations(Object.values(convMap).sort((a, b) => new Date(b.lastMessage.created) - new Date(a.lastMessage.created)));

        } catch (e) {
            console.error("Error loading conversations", e);
        }
    };

    useEffect(() => {
        fetchConversations();
        pb.collection('messages').subscribe('*', function (e) {
            fetchConversations();
        }).catch(e => { });
        return () => pb.collection('messages').unsubscribe('*');
    }, [user.id]);

    // 2. Load Chat
    useEffect(() => {
        if (!selectedUserId) {
            setContextOpen(false);
            setAttachments([]);
            setReplyContent('');
            return;
        }

        const loadChat = async () => {
            setLoading(true);
            try {
                const filter = `from = "${selectedUserId}" || to = "${selectedUserId}"`;
                const res = await pb.collection('messages').getList(1, 50, {
                    filter,
                    sort: 'created',
                    expand: 'from,to'
                });
                setChatMessages(res.items);

                const unreadIds = res.items.filter(m => m.from === selectedUserId && !m.read).map(m => m.id);
                if (unreadIds.length > 0) {
                    await Promise.all(unreadIds.map(id => pb.collection('messages').update(id, { read: true })));
                    fetchConversations();
                }
            } catch (e) {
                console.error("Error loading chat", e);
            } finally {
                setLoading(false);
            }
        };
        loadChat();

        const unsub = pb.collection('messages').subscribe('*', function (e) {
            if (e.action === 'create') {
                const m = e.record;
                if (m.from === selectedUserId || m.to === selectedUserId) {
                    setChatMessages(prev => [...prev, m]);
                    scrollToBottom();
                }
            }
            if (e.action === 'update') {
                const m = e.record;
                if (m.from === selectedUserId || m.to === selectedUserId) {
                    setChatMessages(prev => prev.map(mm => mm.id === m.id ? m : mm));
                }
            }
        });
        return () => unsub;
    }, [selectedUserId, user.id]);

    const scrollToBottom = () => {
        setTimeout(() => {
            if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }, 100);
    };

    useEffect(() => {
        scrollToBottom();
    }, [chatMessages]);

    // 3. Handle Send with Attachment
    const handleSend = async (e) => {
        e.preventDefault();
        if ((!replyContent.trim() && attachments.length === 0) || !selectedUserId) return;

        try {
            // Use FormData for file upload
            const formData = new FormData();
            formData.append('subject', 'Réponse Admin');
            formData.append('content', replyContent);
            formData.append('from', user.id);
            formData.append('to', selectedUserId);
            formData.append('read', 'false');

            const custId = conversations.find(c => c.userId === selectedUserId)?.customer?.id;
            if (custId) formData.append('customer_id', custId);

            attachments.forEach(file => {
                formData.append('attachment', file);
            });

            await pb.collection('messages').create(formData);

            setReplyContent('');
            setAttachments([]);
            if (fileInputRef.current) fileInputRef.current.value = "";
        } catch (e) {
            console.error(e);
            alert("Erreur d'envoi");
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const newFiles = Array.from(e.target.files);
            if (newFiles.length + attachments.length > 10) {
                alert("Maximum 10 fichiers autorisés.");
                return;
            }
            setAttachments(prev => [...prev, ...newFiles]);
        }
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // 4. Handle Context Popup
    const handleOpenContext = async () => {
        if (!selectedUserId) return;
        setContextOpen(true);
        setContextData(prev => ({ ...prev, loading: true, error: null }));

        try {
            const conv = conversations.find(c => c.userId === selectedUserId);

            let customer = conv?.customer;
            let custId = customer?.id;

            if (!custId) {
                let userEmail = conv?.user?.email;
                if (!userEmail && selectedUserId) {
                    try {
                        const u = await pb.collection('users').getOne(selectedUserId);
                        userEmail = u.email;
                    } catch (e) { console.error("User fetch failed", e); }
                }

                if (userEmail) {
                    try {
                        const safeEmail = String(userEmail).replace(/"/g, '\\"');
                        const res = await pb.collection('customers').getList(1, 1, {
                            filter: `(email = "${safeEmail}") || (email ~ "${safeEmail}")`
                        });
                        if (res.items.length > 0) {
                            customer = res.items[0];
                            custId = customer.id;
                        }
                    } catch (e) { console.error("Customer lookup failed", e); }
                }
            }

            if (!custId) {
                let fallbackUser = conv?.user;
                if (!fallbackUser && selectedUserId) {
                    try { fallbackUser = await pb.collection('users').getOne(selectedUserId); } catch { }
                }
                setContextData({ projects: [], invoices: [], loading: false, error: "Pas de fiche client associée (email non trouvé dans Clients)", customer: fallbackUser });
                return;
            }

            const [pRes, iRes] = await Promise.all([
                pb.collection('projects').getList(1, 5, { filter: `customer_id = "${custId}"`, sort: '-updated' }),
                pb.collection('invoices').getList(1, 5, { filter: `customer_id = "${custId}" && status != "paid"`, sort: '-created' })
            ]);

            setContextData({
                projects: pRes.items,
                invoices: iRes.items,
                loading: false,
                error: null,
                customer: customer
            });
        } catch (e) {
            console.error(e);
            setContextData({ projects: [], invoices: [], loading: false, error: "Erreur lors du chargement des données", customer: null });
        }
    };

    // Group messages by date
    const groupedMessages = [];
    let lastDate = null;
    chatMessages.forEach(msg => {
        const dateLabel = getDayLabel(msg.created);
        if (dateLabel !== lastDate) {
            groupedMessages.push({ type: 'date', label: dateLabel, id: 'date-' + msg.id });
            lastDate = dateLabel;
        }
        groupedMessages.push({ type: 'message', data: msg, id: msg.id });
    });

    const isImage = (filename) => {
        if (!filename) return false;
        return /\.(jpg|jpeg|png|gif|webp)$/i.test(filename);
    };

    return (
        <AdminLayout title="Messagerie">
            <div style={{ display: 'flex', height: 'calc(100vh - 140px)', gap: 0, background: 'var(--admin-bg)', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--admin-border)' }}>

                {/* --- LEFT SIDEBAR (CONVERSATIONS) --- */}
                <div style={{ width: 320, display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--admin-border)', background: 'var(--admin-panel)' }}>
                    <div style={{ padding: '20px 16px', borderBottom: '1px solid var(--admin-border)' }}>
                        <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Discussions</h3>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        {conversations.map(c => {
                            const name = c.customer?.name || c.user?.name || c.user?.email || 'Client';
                            const email = c.customer?.email || c.user?.email;
                            const isSelected = selectedUserId === c.userId;

                            return (
                                <div
                                    key={c.userId}
                                    onClick={() => setSelectedUserId(c.userId)}
                                    style={{
                                        padding: '16px',
                                        borderBottom: '1px solid var(--admin-border)',
                                        background: isSelected ? 'rgba(79, 70, 229, 0.08)' : 'transparent',
                                        cursor: 'pointer',
                                        display: 'flex', gap: 12, alignItems: 'center',
                                        transition: 'background 0.2s',
                                        position: 'relative'
                                    }}
                                    onMouseEnter={(e) => !isSelected && (e.currentTarget.style.background = 'var(--admin-panel-2)')}
                                    onMouseLeave={(e) => !isSelected && (e.currentTarget.style.background = 'transparent')}
                                >
                                    <Avatar name={name} email={email} size={42} />
                                    <div style={{ flex: 1, overflow: 'hidden' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                            <span style={{ fontWeight: c.unreadCount > 0 ? 700 : 500, color: 'var(--admin-text)', fontSize: '0.95rem' }}>{name}</span>
                                            <span style={{ fontSize: '0.75rem', color: 'var(--admin-muted)' }}>
                                                {new Date(c.lastMessage.created).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{
                                                fontSize: '0.85rem', color: c.unreadCount > 0 ? 'var(--admin-text)' : 'var(--admin-muted)',
                                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180
                                            }}>
                                                {c.lastMessage.attachment && c.lastMessage.attachment.length > 0 ? '📎 Pièce(s) jointe(s)' : c.lastMessage.content}
                                            </div>
                                            {c.unreadCount > 0 && (
                                                <span style={{
                                                    background: '#ef4444', color: 'white',
                                                    fontSize: '0.7rem', fontWeight: 700,
                                                    minWidth: 20, height: 20, borderRadius: 10,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    marginLeft: 6
                                                }}>
                                                    {c.unreadCount}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {isSelected && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: '#4f46e5' }} />}
                                </div>
                            );
                        })}
                        {conversations.length === 0 && <div style={{ padding: 40, color: 'gray', textAlign: 'center', fontStyle: 'italic' }}>Aucune discussion active</div>}
                    </div>
                </div>

                {/* --- RIGHT AREA (CHAT) --- */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#f8fafc', position: 'relative' }}>
                    {selectedUserId ? (
                        <>
                            {/* Header */}
                            <div style={{
                                padding: '12px 24px',
                                borderBottom: '1px solid var(--admin-border)',
                                background: 'white',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <Avatar
                                        name={conversations.find(c => c.userId === selectedUserId)?.customer?.name || conversations.find(c => c.userId === selectedUserId)?.user?.name}
                                        email={conversations.find(c => c.userId === selectedUserId)?.user?.email}
                                        size={40}
                                    />
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: '1rem' }}>
                                            {(() => {
                                                const c = conversations.find(cc => cc.userId === selectedUserId);
                                                return c?.customer?.name || c?.user?.name || c?.user?.email || 'Client';
                                            })()}
                                        </div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--admin-muted)' }}>
                                            {conversations.find(c => c.userId === selectedUserId)?.customer?.company || 'Discussion client'}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={handleOpenContext}
                                    style={{
                                        background: 'transparent', border: '1px solid #e2e8f0',
                                        color: '#64748b', borderRadius: 8, padding: '8px 16px', cursor: 'pointer',
                                        fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 6,
                                        transition: 'all 0.2s'
                                    }}
                                    className="hover-btn"
                                    title="Voir le contexte client"
                                >
                                    ℹ️ Infos & Contexte
                                </button>
                            </div>

                            {/* Messages List */}
                            <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '20px 40px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {groupedMessages.map(item => {
                                    if (item.type === 'date') {
                                        return (
                                            <div key={item.id} style={{ alignSelf: 'center', margin: '20px 0 10px 0', fontSize: '0.75rem', color: '#94a3b8', background: '#f1f5f9', padding: '4px 12px', borderRadius: 99 }}>
                                                {item.label}
                                            </div>
                                        );
                                    }
                                    const msg = item.data;
                                    const isMe = msg.from === user.id;
                                    return (
                                        <div key={msg.id} style={{ alignSelf: isMe ? 'flex-end' : 'flex-start', maxWidth: '65%', position: 'relative' }}>
                                            <div style={{
                                                background: isMe ? '#4f46e5' : 'white',
                                                color: isMe ? 'white' : '#1e293b',
                                                padding: '12px 16px',
                                                borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                                boxShadow: isMe ? '0 4px 6px -1px rgba(79, 70, 229, 0.2)' : '0 1px 3px rgba(0,0,0,0.1)',
                                                border: isMe ? 'none' : '1px solid #e2e8f0',
                                                fontSize: '0.95rem',
                                                lineHeight: '1.5'
                                            }}>
                                                {(() => {
                                                    const atts = Array.isArray(msg.attachment) ? msg.attachment : (msg.attachment ? [msg.attachment] : []);
                                                    if (atts.length === 0) return null;

                                                    return (
                                                        <div style={{ marginBottom: msg.content ? 8 : 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                            {atts.map((filename, idx) => (
                                                                <div key={idx}>
                                                                    {isImage(filename) ? (
                                                                        <a href={pb.files.getUrl(msg, filename)} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
                                                                            <img
                                                                                src={pb.files.getUrl(msg, filename)}
                                                                                alt="attachment"
                                                                                style={{ maxWidth: '100%', borderRadius: 8, maxHeight: 300, display: 'block' }}
                                                                            />
                                                                        </a>
                                                                    ) : (
                                                                        <a href={pb.files.getUrl(msg, filename)} target="_blank" rel="noreferrer"
                                                                            style={{
                                                                                display: 'flex', alignItems: 'center', gap: 8,
                                                                                background: isMe ? 'rgba(255,255,255,0.2)' : '#f1f5f9',
                                                                                padding: '8px 12px', borderRadius: 8,
                                                                                color: isMe ? 'white' : '#1e293b', textDecoration: 'none', fontWeight: 500
                                                                            }}>
                                                                            <span style={{ fontSize: '1.2rem' }}>📎</span>
                                                                            <span style={{ textDecoration: 'underline' }}>{filename}</span>
                                                                        </a>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    );
                                                })()}
                                                {msg.content}
                                            </div>
                                            <div style={{
                                                fontSize: '0.7rem', color: '#94a3b8', marginTop: 4,
                                                textAlign: isMe ? 'right' : 'left', display: 'flex', alignItems: 'center', justifyContent: isMe ? 'flex-end' : 'flex-start', gap: 4
                                            }}>
                                                {new Date(msg.created).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                {isMe && (
                                                    <span style={{ fontSize: '0.85rem', color: msg.read ? '#10b981' : '#cbd5e1' }}>
                                                        {msg.read ? '✓✓' : '✓'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Type Area with File Upload */}
                            <div style={{ padding: 20, background: 'white', borderTop: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <input
                                    type="file"
                                    multiple
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    style={{ display: 'none' }}
                                />

                                {attachments.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
                                        {attachments.map((file, idx) => (
                                            <div key={idx} style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                                background: '#eff6ff', border: '1px solid #bfdbfe',
                                                padding: '4px 8px', borderRadius: 6,
                                                fontSize: '0.8rem', color: '#1e40af'
                                            }}>
                                                <span style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1e40af', fontSize: '1.1rem', padding: 0, lineHeight: 1 }}
                                                >×</button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                                    <div style={{ flex: 1, position: 'relative', display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                                        {/* SNIPPETS POPUP */}
                                        {showSnippets && (
                                            <div style={{
                                                position: 'absolute', bottom: '100%', left: 0, marginBottom: 16,
                                                background: 'white', border: '1px solid #e2e8f0', borderRadius: 12,
                                                boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
                                                width: 320, overflow: 'hidden', zIndex: 10, display: 'flex', flexDirection: 'column'
                                            }}>
                                                <div style={{ padding: '10px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.05em' }}>
                                                    RÉPONSES RAPIDES
                                                </div>
                                                <div style={{ maxHeight: 250, overflowY: 'auto' }}>
                                                    {QUICK_REPLIES.map((s, i) => (
                                                        <button
                                                            key={i}
                                                            onClick={() => handleAddSnippet(s.text)}
                                                            style={{
                                                                width: '100%', textAlign: 'left', padding: '12px 16px', background: 'white', border: 'none',
                                                                borderBottom: i === QUICK_REPLIES.length - 1 ? 'none' : '1px solid #f1f5f9',
                                                                cursor: 'pointer', fontSize: '0.9rem', color: '#334155', transition: 'background 0.1s',
                                                                display: 'flex', alignItems: 'center', gap: 8
                                                            }}
                                                            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                                            onMouseLeave={e => e.currentTarget.style.background = 'white'}
                                                        >
                                                            <span style={{ fontSize: '1.2rem' }}>{s.label.split(' ')[0]}</span>
                                                            <span style={{ fontWeight: 500 }}>{s.label.split(' ').slice(1).join(' ')}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <button
                                            type="button"
                                            onClick={() => setShowSnippets(prev => !prev)}
                                            style={{
                                                background: showSnippets ? '#e0e7ff' : '#f1f5f9', border: 'none',
                                                width: 44, height: 44, borderRadius: '50%',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                cursor: 'pointer', flexShrink: 0, color: showSnippets ? '#4f46e5' : '#64748b', fontSize: '1.2rem',
                                                marginBottom: 2, transition: 'all 0.2s'
                                            }}
                                            title="Réponses rapides"
                                        >
                                            ⚡
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current.click()}
                                            style={{
                                                background: '#f1f5f9', border: 'none',
                                                width: 44, height: 44, borderRadius: '50%',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                cursor: 'pointer', flexShrink: 0, color: '#64748b', fontSize: '1.3rem',
                                                marginBottom: 2
                                            }}
                                            title="Joindre des fichiers (max 10)"
                                        >
                                            📎
                                        </button>
                                        <textarea
                                            value={replyContent}
                                            onChange={e => setReplyContent(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    handleSend(e);
                                                }
                                            }}
                                            placeholder="Écrivez votre message..."
                                            style={{
                                                flex: 1, padding: '14px', borderRadius: 12,
                                                border: '1px solid #e2e8f0', background: '#f8fafc',
                                                color: '#334155', fontSize: '0.95rem',
                                                resize: 'none', height: replyContent.length > 100 ? 80 : 50,
                                                fontFamily: 'inherit',
                                                transition: 'height 0.2s',
                                                outline: 'none'
                                            }}
                                            onFocus={e => e.target.style.borderColor = '#4f46e5'}
                                            onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                                        />
                                    </div>
                                    <button
                                        onClick={handleSend}
                                        style={{
                                            background: '#4f46e5', color: 'white', border: 'none',
                                            width: 50, height: 50, borderRadius: '50%',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            cursor: 'pointer', flexShrink: 0,
                                            boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.3)',
                                            transition: 'transform 0.1s'
                                        }}
                                        onMouseDown={e => e.currentTarget.style.transform = 'scale(0.95)'}
                                        onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
                                    </button>
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#cbd5e1', textAlign: 'center', marginLeft: 56 }}>
                                    Entrée pour envoyer
                                </div>
                            </div>
                        </>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8' }}>
                            <div style={{ fontSize: '4rem', marginBottom: 16 }}>💬</div>
                            <div style={{ fontSize: '1.2rem', fontWeight: 500 }}>Sélectionnez une conversation</div>
                            <p style={{ marginTop: 8 }}>Pour démarrer un échange avec un client</p>
                        </div>
                    )}

                    {/* CONTEXT POPUP */}
                    {contextOpen && (
                        <div style={{
                            position: 'absolute', inset: 0,
                            background: 'rgba(255,255,255,0.6)',
                            zIndex: 50,
                            display: 'flex', justifyContent: 'flex-end',
                            backdropFilter: 'blur(4px)'
                        }}>
                            <div style={{
                                background: 'white',
                                width: 400, height: '100%',
                                boxShadow: '-10px 0 25px rgba(0,0,0,0.1)',
                                display: 'flex', flexDirection: 'column',
                                borderLeft: '1px solid #e2e8f0',
                                animation: 'slideInRight 0.3s ease-out'
                            }}>
                                <div style={{ padding: 20, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <strong>Contexte Client</strong>
                                    <button onClick={() => setContextOpen(false)} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 24, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
                                </div>

                                <div style={{ padding: 24, overflowY: 'auto', flex: 1, background: '#f8fafc' }}>
                                    {contextData.loading ? (
                                        <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>Chargement...</div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

                                            <div>
                                                <h4 style={{ marginTop: 0, marginBottom: 12, color: '#64748b', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Fiche Contact</h4>
                                                <div style={{ background: 'white', padding: 16, borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                                                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
                                                        <Avatar name={contextData.customer?.name} size={40} />
                                                        <div>
                                                            <div style={{ fontWeight: 700, fontSize: '1rem' }}>{contextData.customer?.name || 'Nom Inconnu'}</div>
                                                            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{contextData.customer?.company || 'Particulier'}</div>
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.9rem', color: '#334155' }}>
                                                        {contextData.customer?.email && <div style={{ display: 'flex', gap: 8 }}><span>📧</span> {contextData.customer.email}</div>}
                                                        {contextData.customer?.phone && <div style={{ display: 'flex', gap: 8 }}><span>📞</span> {contextData.customer.phone}</div>}
                                                        {contextData.customer?.address && <div style={{ display: 'flex', gap: 8 }}><span>📍</span> {contextData.customer.address} {contextData.customer.city}</div>}
                                                    </div>
                                                </div>
                                            </div>

                                            <div>
                                                <h4 style={{ marginTop: 0, marginBottom: 12, color: '#64748b', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Projets Récents</h4>
                                                {contextData.projects.length === 0 ? <div style={{ fontStyle: 'italic', color: '#94a3b8', fontSize: '0.9rem' }}>Aucun projet récent.</div> : (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                        {contextData.projects.map(p => (
                                                            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', padding: '10px 12px', borderRadius: 8, fontSize: '0.9rem', border: '1px solid #e2e8f0' }}>
                                                                <span style={{ fontWeight: 500 }}>{p.title || 'Projet'}</span>
                                                                <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: 99, background: p.status === 'completed' ? '#dcfce7' : '#e0e7ff', color: p.status === 'completed' ? '#166534' : '#3730a3' }}>
                                                                    {p.status}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            <div>
                                                <h4 style={{ marginTop: 0, marginBottom: 12, color: '#64748b', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Factures en Attente</h4>
                                                {contextData.invoices.length === 0 ? (
                                                    <div style={{ background: '#ecfdf5', color: '#047857', padding: '12px', borderRadius: 8, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #a7f3d0' }}>
                                                        ✅ À jour
                                                    </div>
                                                ) : (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                        {contextData.invoices.map(i => (
                                                            <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fef2f2', border: '1px solid #fecaca', padding: '10px 12px', borderRadius: 8, fontSize: '0.9rem' }}>
                                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                    <span style={{ color: '#b91c1c', fontWeight: 600 }}>#{i.invoice_number || i.id.slice(0, 6)}</span>
                                                                    <span style={{ fontSize: '0.75rem', color: '#991b1b' }}>{new Date(i.created).toLocaleDateString()}</span>
                                                                </div>
                                                                <span style={{ fontWeight: 700, color: '#b91c1c' }}>{i.total?.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                        </div>
                                    )}
                                </div>
                            </div>
                            <style>{`
                                @keyframes slideInRight {
                                    from { transform: translateX(100%); }
                                    to { transform: translateX(0); }
                                }
                             `}</style>
                        </div>
                    )}

                </div>
            </div>
        </AdminLayout>
    );
}
